<script setup lang="ts" generic="T extends {}">
// https://github.com/uct8086/vue-virtual-list/tree/master
// https://github.com/reactjser/vue3-virtual-scroll-list/blob/main/src/virtual-list.tsx
// missing page mode - heres how to do
import { watch, onActivated, onMounted, ref, nextTick } from 'vue'
import GwScrollCont, {
  type GwScrollContResizeProps,
  type GwScrollContProps,
} from '@/components/lib/GwScrollCont/GwScrollCont.vue'
import GwVirtualListItemDynamicSize from '@/components/lib/GwVirtualList/GwVirtualListItemDynamicSize.vue'
import GwVirtual from '@/components/lib/GwVirtualList/gwvirtual'

export type GwVirtualListProps<T> = {
  idKey: keyof T
  items: T[]
  direction?: 'vertical' | 'horizontal'
  dynamicMinSize?: number
  gwScrollContProps?: GwScrollContProps
  hasDynamicSize?: boolean
  itemSize?: number
  offset?: number
  start?: number
} & Partial<Pick<GwScrollContProps, 'hasDragScroll'>>

export interface ResizeEmitProps<T> {
  el: HTMLElement
  item: T
}

const props = withDefaults(defineProps<GwVirtualListProps<T>>(), {
  direction: 'vertical',
  itemSize: 20,
  dynamicMinSize: 50,
  start: 0,
  offset: 0,
  gwScrollContProps: () => ({
    hasEdgeAutoScroll: false,
    hasOverflowSurfaces: false,
    hasDragScroll: false,
  }),
})

const emit = defineEmits([
  'scroll',
  'visible',
  'scroll:update',
  'scrolled:end',
  'scrolled:start',
  'update:renderedItems',
])

const isHorizontal = props.direction === 'horizontal'
const directionKey = isHorizontal ? 'scrollLeft' : 'scrollTop'
const clientSizeKey = isHorizontal ? 'clientWidth' : 'clientHeight'
const scrollKey = isHorizontal ? 'scrollWidth' : 'scrollHeight'

const sizeBasedRenderedItems = ref(0) // is initialized and updated by handleResize
const scrollContRef = ref<InstanceType<typeof GwScrollCont> | null>(null)

const rangeRef = ref(Object.create(null))
const visibleItems = ref<T[]>()
const verticalVals = ref({
  top: '0px',
  bottom: '0px',
})
const horizontalVals = ref({
  left: '0px',
  right: '0px',
})
const scrollContStyle = ref<any>({})

let virtual: any = null

const getUniqueIdFromitems = () => props.items.map(item => item[props.idKey])

const saveSizes = () => {
  if (!props.hasDynamicSize) {
    props.items.forEach(item => {
      virtual.saveSize(item[props.idKey], props.itemSize)
    })
  }
}

const setVals = range => {
  const { start, end, padFront, padBehind } = range

  rangeRef.value = range
  visibleItems.value = props.items.slice(start, end + 1)
  verticalVals.value = {
    top: `${padFront}px`,
    bottom: `${padBehind}px`,
  }
  horizontalVals.value = {
    left: `${padFront}px`,
    right: `${padBehind}px`,
  }
  scrollContStyle.value = isHorizontal
    ? {
        'padding-left': horizontalVals.value.left,
        'padding-right': horizontalVals.value.right,
      }
    : {
        'padding-top': verticalVals.value.top,
        'padding-bottom': verticalVals.value.bottom,
      }
}

const installVirtual = () => {
  virtual = new GwVirtual(
    {
      slotHeaderSize: 0,
      slotFooterSize: 0,
      uniqueIds: getUniqueIdFromitems(),
      dynamicMinSize: props.dynamicMinSize,
      // set by handleResize
      renderedItems: 0,
      buffer: 0,
    },
    (range: Array<any>) => {
      setVals(range)
    },
  )
  saveSizes()
  // sync initial range
  rangeRef.value = virtual.getRange()
}

installVirtual()

const handleResize = (args: GwScrollContResizeProps) => {
  const { height, width } = args
  const minSize = props.hasDynamicSize ? props.dynamicMinSize : props.itemSize
  let res = 0
  if (props.direction === 'vertical') {
    res = Math.ceil(height / minSize)
  } else {
    res = Math.ceil(width / minSize)
  }
  res += Math.min(res * 2, 30)
  sizeBasedRenderedItems.value = res

  virtual.updateParam('renderedItems', res)
  virtual.updateParam('buffer', Math.round(res / 3))
  emit('update:renderedItems', res)
}

const scrollToOffset = (offset: number) => {
  if (scrollContRef.value) {
    scrollContRef.value.scrollToOffset(offset)
  }
}
const checkOverflow = () => {
  if (scrollContRef.value) {
    scrollContRef.value.checkOverflow()
  }
}

const scrollToBottom = () => {
  scrollToOffset(999999999999999 * 999999999999999)
}
const scrollToIndex = (index: number) => {
  if (index >= props.items.length - 1) {
    scrollToBottom()
  } else {
    const offset = virtual.getOffset(index)
    scrollToOffset(offset)
  }
}
defineExpose({
  scrollToOffset,
})

const onItemResized = ({ item, el }: ResizeEmitProps<T>) => {
  const shapeKey = isHorizontal ? 'offsetWidth' : 'offsetHeight'
  const size = el[shapeKey] || 0
  const id = item[props.idKey]
  virtual.saveSize(id, size)
}
const emitUpdate = (e: Event) => {
  emit('scroll:update', e, visibleItems.value)
}
const handleScroll = (e: Event) => {
  emit('scroll', e)
  const offset = (e.target as HTMLElement)[directionKey]
  const clientSize = (e.target as HTMLElement)[clientSizeKey]
  const scrollSize = (e.target as HTMLElement)[scrollKey]

  // iOS scroll-spring-back behavior will make direction mistake
  if (offset < 0 || offset + clientSize > scrollSize + 1 || !scrollSize) {
    return
  }

  virtual.handleScroll(offset)
  emitUpdate(e)
}

watch(
  () => props.items,
  () => {
    virtual.updateParam('uniqueIds', getUniqueIdFromitems())
    virtual.handleitemsChange()
    saveSizes()
    nextTick(() => {
      checkOverflow()
    })
  },
)
watch(
  () => sizeBasedRenderedItems.value,
  newValue => {
    virtual.updateParam('renderedItems', newValue)
    virtual.handleSlotSizeChange()
  },
)
watch(
  () => props.start,
  newValue => {
    scrollToIndex(newValue)
  },
)
watch(
  () => props.offset,
  newValue => {
    scrollToOffset(newValue)
  },
)

onActivated(() => {
  scrollToOffset(virtual.offset)
})

onMounted(() => {
  emit('visible')
  // set position
  nextTick(() => {
    if (props.start) {
      scrollToIndex(props.start)
      return
    }
    if (props.offset) {
      scrollToOffset(props.offset)
    }
  })
})
</script>

<template>
  <GwScrollCont
    ref="scrollContRef"
    class="gwvirlist"
    :direction="props.direction"
    :hasResizeObserver="true"
    v-bind="props.gwScrollContProps"
    @scroll="handleScroll"
    @scrolled:start="emit('scrolled:start')"
    @scrolled:end="emit('scrolled:end')"
    @resized="handleResize"
  >
    <template #content_before>
      <slot name="content_before"></slot>
    </template>
    <template #default="{ overflow }">
      <slot
        name="default"
        :verticalVals
        :horizontalVals
        :scrollContStyle
        :visibleItems
        :overflow
        :startIndex="rangeRef.start"
        :methods="{ onItemResized }"
      >
        <div
          :style="scrollContStyle"
          data-tst="group"
          class="gwvirlist_group"
          :class="`gwvirlist_group_${direction}`"
        >
          <GwVirtualListItemDynamicSize
            v-for="(item, index) in hasDynamicSize ? visibleItems : []"
            :key="index"
            @resized="el => onItemResized({ item, el })"
          >
            <slot
              name="item"
              :item
              :index="rangeRef.start + index"
            />
          </GwVirtualListItemDynamicSize>
          <template
            v-for="(item, index) in !hasDynamicSize ? visibleItems : []"
            :key="index"
          >
            <slot
              name="item"
              :item
              :index="rangeRef.start + index"
            />
          </template>
        </div>
      </slot>
    </template>

    <template #content_after>
      <slot name="content_after"></slot>
    </template>
  </GwScrollCont>
</template>

<style lang="scss" scoped>
.gwvirlist {
  display: flex;
  &_group {
    flex: 1;
    &_vertical {
      display: flex;
      flex-direction: column;
    }
    &_horizontal {
      display: flex;
    }
  }
}
</style>
